import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import { getUserRequested, updateUserRequested, updatePasswordRequested } from "../../actions/userAction";
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline';
import { useNavigate } from "react-router-dom";

const Settings = ({
    user,
    currentUser,
    userGetRequested,
    getUserRequested: getUser,
    updateUserRequested: updateUser,
    updatePasswordRequested: updatePassword,
}) => {
    const navigate = useNavigate();
    const id = user?.id;

    const [activeTab, setActiveTab] = useState('profile');
    const [profileForm, setProfileForm] = useState({ name: '', email: '', username: '' });
    const [passwordForm, setPasswordForm] = useState({ currentPassword: '', password: '', verifyPassword: '' });
    const [profileMessage, setProfileMessage] = useState('');
    const [passwordMessage, setPasswordMessage] = useState('');

    useEffect(() => {
        if (!id || userGetRequested) {
            return;
        }
        getUser({ id });
    }, [id, userGetRequested, getUser]);

    useEffect(() => {
        if (!currentUser) {
            return;
        }
        setProfileForm({
            name: currentUser?.name || '',
            email: currentUser?.email || '',
            username: currentUser?.username || '',
        });
    }, [currentUser]);

    if (!user || !user.id) {
        navigate('/login');
        return null;
    }

    const onProfileChange = (event) => {
        const { name, value } = event.target;
        setProfileMessage('');
        setProfileForm((values) => ({ ...values, [name]: value }));
    };

    const onPasswordChange = (event) => {
        const { name, value } = event.target;
        setPasswordMessage('');
        setPasswordForm((values) => ({ ...values, [name]: value }));
    };

    const onUpdateProfile = () => {
        const payload = { ...profileForm, id };
        updateUser(payload);
        setProfileMessage('Profile update submitted.');
    };

    const onUpdatePassword = () => {
        if (!passwordForm.currentPassword || !passwordForm.password || !passwordForm.verifyPassword) {
            setPasswordMessage('All password fields are required.');
            return;
        }

        if (passwordForm.password !== passwordForm.verifyPassword) {
            setPasswordMessage('New password and confirm password do not match.');
            return;
        }

        updatePassword({
            currentPassword: passwordForm.currentPassword,
            password: passwordForm.password,
        });

        setPasswordForm({ currentPassword: '', password: '', verifyPassword: '' });
        setPasswordMessage('Password update submitted.');
    };

    const tabs = [
        { key: 'profile', label: 'User Details' },
        { key: 'password', label: 'Password' },
        { key: 'automation', label: 'Automation', disabled: true },
    ];

    return (
        <div className="h-full bg-gray-100 pb-8 text-gray-900">
            <main className="max-w-full px-4 pt-4 sm:px-6 lg:px-8">
                <div className="flex flex-row content-start justify-start">
                    <h1 className="text-xl font-semibold">Settings</h1>
                </div>
                <div className="mt-6">
                    <ul className="flex flex-wrap border-b border-gray-200 text-sm font-medium text-center text-gray-500">
                        {tabs.map((tab) => (
                            <li key={tab.key} className="mr-2">
                                <button
                                    type="button"
                                    disabled={tab.disabled}
                                    onClick={() => !tab.disabled && setActiveTab(tab.key)}
                                    className={`inline-block rounded-t-lg p-4 ${
                                        tab.disabled
                                            ? 'cursor-not-allowed text-gray-400'
                                            : activeTab === tab.key
                                                ? 'bg-white text-gray-800'
                                                : 'text-gray-500 hover:text-gray-700'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            </li>
                        ))}
                    </ul>

                    {activeTab === 'profile' && (
                        <ProfileForm
                            formData={profileForm}
                            onChange={onProfileChange}
                            onSubmit={onUpdateProfile}
                            message={profileMessage}
                        />
                    )}

                    {activeTab === 'password' && (
                        <PasswordForm
                            formData={passwordForm}
                            onChange={onPasswordChange}
                            onSubmit={onUpdatePassword}
                            message={passwordMessage}
                        />
                    )}
                </div>
            </main>
        </div>
    );
};

const ProfileForm = ({ formData, onChange, onSubmit, message }) => (
    <div className="mt-6 w-auto max-w-sm">
        <form className="space-y-3">
            <div>
                <label htmlFor="name" className="block text-sm font-medium leading-6 text-gray-900">Name</label>
                <div className="mt-2">
                    <input
                        id="name"
                        name="name"
                        type="text"
                        required
                        className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                        value={formData?.name || ""}
                        onChange={onChange}
                    />
                </div>
            </div>

            <div>
                <label htmlFor="email" className="block text-sm font-medium leading-6 text-gray-900">Email</label>
                <div className="mt-2">
                    <input
                        id="email"
                        name="email"
                        type="email"
                        required
                        className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                        value={formData?.email || ""}
                        onChange={onChange}
                    />
                </div>
            </div>

            <div>
                <label htmlFor="username" className="block text-sm font-medium leading-6 text-gray-900">Username</label>
                <div className="mt-2">
                    <input
                        id="username"
                        name="username"
                        type="text"
                        required
                        className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                        value={formData?.username || ""}
                        onChange={onChange}
                    />
                </div>
            </div>

            {message && <p className="m-0 text-sm text-emerald-600">{message}</p>}

            <div className="text-right">
                <button
                    type="button"
                    className="mb-1 mr-1 rounded bg-emerald-500 px-6 py-3 text-sm font-bold uppercase text-white shadow outline-none transition-all duration-150 ease-linear hover:shadow-lg focus:outline-none active:bg-emerald-600"
                    onClick={onSubmit}
                >
                    Update Profile
                </button>
            </div>
        </form>
    </div>
);

const PasswordForm = ({ formData, onChange, onSubmit, message }) => {
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showVerifyPassword, setShowVerifyPassword] = useState(false);

    return (
        <div className="mt-6 w-auto max-w-sm">
            <form className="space-y-3">
                <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium leading-6 text-gray-900">Current Password</label>
                    <div className="relative mt-2">
                        <input
                            id="currentPassword"
                            name="currentPassword"
                            type={showCurrentPassword ? "text" : "password"}
                            required
                            className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                            value={formData?.currentPassword || ""}
                            onChange={onChange}
                        />
                        <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            onClick={() => setShowCurrentPassword((prev) => !prev)}
                        >
                            {showCurrentPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                <div>
                    <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-900">New Password</label>
                    <div className="relative mt-2">
                        <input
                            id="password"
                            name="password"
                            type={showPassword ? "text" : "password"}
                            required
                            className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                            value={formData?.password || ""}
                            onChange={onChange}
                        />
                        <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            onClick={() => setShowPassword((prev) => !prev)}
                        >
                            {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                <div>
                    <label htmlFor="verifyPassword" className="block text-sm font-medium leading-6 text-gray-900">Confirm New Password</label>
                    <div className="relative mt-2">
                        <input
                            id="verifyPassword"
                            name="verifyPassword"
                            type={showVerifyPassword ? "text" : "password"}
                            required
                            className="block w-full rounded-md border-0 px-2 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-green-600 sm:text-sm sm:leading-6"
                            value={formData?.verifyPassword || ""}
                            onChange={onChange}
                        />
                        <button
                            type="button"
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                            onClick={() => setShowVerifyPassword((prev) => !prev)}
                        >
                            {showVerifyPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        </button>
                    </div>
                </div>

                {message && <p className="m-0 text-sm text-emerald-600">{message}</p>}

                <div className="text-right">
                    <button
                        type="button"
                        className="mb-1 mr-1 rounded bg-emerald-500 px-6 py-3 text-sm font-bold uppercase text-white shadow outline-none transition-all duration-150 ease-linear hover:shadow-lg focus:outline-none active:bg-emerald-600"
                        onClick={onSubmit}
                    >
                        Update Password
                    </button>
                </div>
            </form>
        </div>
    );
};

Settings.propTypes = {
    updatePasswordRequested: PropTypes.func,
    updateUserRequested: PropTypes.func,
    user: PropTypes.object,
    userGetRequested: PropTypes.bool,
    currentUser: PropTypes.object,
};

Settings.defaultProps = {
    userGetRequested: false,
    currentUser: {},
};

const mapStateToProps = (state) => ({
    currentUser: state.user.user,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
    updateUserRequested,
    getUserRequested,
    updatePasswordRequested,
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(Settings);
